import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Heart, ArrowLeft, RefreshCw, Bell, BellOff, CheckCircle, 
  AlertTriangle, AlertCircle, XCircle, Database, CreditCard, 
  Mail, Activity, Clock, ChevronDown, ChevronUp, Filter,
  Check, X, Settings, Server, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = '/api';

// Severity badge component
const SeverityBadge = ({ severity }) => {
  const config = {
    critical: { color: 'bg-red-500', icon: XCircle, label: 'Critical' },
    warning: { color: 'bg-orange-500', icon: AlertTriangle, label: 'Warning' },
    info: { color: 'bg-blue-500', icon: AlertCircle, label: 'Info' }
  };
  
  const { color, icon: Icon, label } = config[severity] || config.info;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${color}`}>
      <Icon size={12} />
      {label}
    </span>
  );
};

// Health status indicator
const HealthIndicator = ({ status }) => {
  const config = {
    healthy: { color: 'bg-green-500', pulse: false },
    degraded: { color: 'bg-yellow-500', pulse: true },
    unhealthy: { color: 'bg-red-500', pulse: true },
    unknown: { color: 'bg-gray-500', pulse: false },
    attention_needed: { color: 'bg-orange-500', pulse: true },
    unconfigured: { color: 'bg-gray-400', pulse: false }
  };
  
  const { color, pulse } = config[status] || config.unknown;
  
  return (
    <span className={`inline-block w-3 h-3 rounded-full ${color} ${pulse ? 'animate-pulse' : ''}`} />
  );
};

// Log entry component with expandable details
const LogEntry = ({ log, onAcknowledge }) => {
  const [expanded, setExpanded] = useState(false);
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  const eventTypeLabels = {
    api_error: 'API Error',
    payment_failure: 'Payment Failed',
    signup_error: 'Signup Error',
    webhook_error: 'Webhook Error',
    email_failure: 'Email Failed',
    new_signup: 'New Signup'
  };
  
  return (
    <div className={`border rounded-lg mb-2 overflow-hidden ${log.acknowledged ? 'opacity-60' : ''} ${
      log.severity === 'critical' ? 'border-red-300 bg-red-50' :
      log.severity === 'warning' ? 'border-orange-300 bg-orange-50' :
      'border-gray-200 bg-white'
    }`}>
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <SeverityBadge severity={log.severity} />
          <span className="text-sm font-medium text-gray-700">
            {eventTypeLabels[log.event_type] || log.event_type}
          </span>
          <span className="text-sm text-gray-500 truncate max-w-md">
            {log.message}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">
            {formatTime(log.timestamp)}
          </span>
          {!log.acknowledged && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onAcknowledge(log.id);
              }}
              className="text-xs"
            >
              <Check size={14} className="mr-1" />
              Ack
            </Button>
          )}
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 border-t bg-gray-50">
          <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
            {log.endpoint && (
              <div>
                <span className="text-gray-500">Endpoint:</span>
                <span className="ml-2 font-mono text-gray-700">{log.endpoint}</span>
              </div>
            )}
            {log.user_email && (
              <div>
                <span className="text-gray-500">User:</span>
                <span className="ml-2 text-gray-700">{log.user_email}</span>
              </div>
            )}
          </div>
          {log.details && Object.keys(log.details).length > 0 && (
            <div className="mt-3">
              <span className="text-sm text-gray-500">Details:</span>
              <pre className="mt-1 p-3 bg-gray-900 text-green-400 rounded text-xs overflow-x-auto">
                {JSON.stringify(log.details, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminMonitoring = ({ user }) => {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [severityCounts, setSeverityCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showUnacknowledgedOnly, setShowUnacknowledgedOnly] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);
  const previousCriticalCount = useRef(0);

  const getAdminId = useCallback(() => {
    if (user?.id) return user.id;
    try {
      const savedUser = localStorage.getItem('vowflow_user');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        return parsed?.id;
      }
    } catch (e) {}
    return null;
  }, [user]);

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
      if (permission === 'granted') {
        toast.success('Browser notifications enabled');
      }
    }
  };

  // Show browser notification
  const showNotification = (title, body, severity) => {
    if (notificationsEnabled && 'Notification' in window) {
      const icon = severity === 'critical' ? '🔴' : severity === 'warning' ? '🟠' : '🔵';
      new Notification(`${icon} ${title}`, {
        body,
        icon: '/favicon.ico',
        tag: 'vowflow-admin'
      });
    }
  };

  const fetchHealth = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) return;
    
    try {
      const response = await axios.get(`${API}/admin/system-health`, {
        headers: { 'admin-id': adminId }
      });
      setHealth(response.data);
      
      // Check for new critical issues and notify
      const newCriticalCount = response.data.unacknowledged_critical || 0;
      if (newCriticalCount > previousCriticalCount.current && previousCriticalCount.current > 0) {
        showNotification(
          'New Critical Alert',
          `${newCriticalCount - previousCriticalCount.current} new critical issue(s) detected`,
          'critical'
        );
      }
      previousCriticalCount.current = newCriticalCount;
      
    } catch (error) {
      console.error('Failed to fetch health:', error);
    }
  }, [getAdminId, notificationsEnabled]);

  const fetchLogs = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) return;
    
    try {
      const params = new URLSearchParams();
      if (filterSeverity !== 'all') params.append('severity', filterSeverity);
      if (filterType !== 'all') params.append('event_type', filterType);
      if (showUnacknowledgedOnly) params.append('unacknowledged_only', 'true');
      params.append('limit', '50');
      
      const response = await axios.get(`${API}/admin/system-logs?${params}`, {
        headers: { 'admin-id': adminId }
      });
      setLogs(response.data.logs);
      setLogsTotal(response.data.total);
      setSeverityCounts(response.data.severity_counts);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  }, [getAdminId, filterSeverity, filterType, showUnacknowledgedOnly]);

  const fetchNotificationSettings = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) return;
    
    try {
      const response = await axios.get(`${API}/admin/notification-settings`, {
        headers: { 'admin-id': adminId }
      });
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    }
  }, [getAdminId]);

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.all([fetchHealth(), fetchLogs()]);
    setLastRefresh(new Date());
    setRefreshing(false);
  };

  const acknowledgeLog = async (logId) => {
    const adminId = getAdminId();
    if (!adminId) return;
    
    try {
      await axios.post(`${API}/admin/system-logs/${logId}/acknowledge`, {}, {
        headers: { 'admin-id': adminId }
      });
      toast.success('Log acknowledged');
      fetchLogs();
      fetchHealth();
    } catch (error) {
      toast.error('Failed to acknowledge log');
    }
  };

  const acknowledgeAll = async (severity = null) => {
    const adminId = getAdminId();
    if (!adminId) return;
    
    try {
      const params = severity ? { severity } : {};
      const response = await axios.post(`${API}/admin/system-logs/acknowledge-all`, params, {
        headers: { 'admin-id': adminId }
      });
      toast.success(`Acknowledged ${response.data.acknowledged_count} logs`);
      fetchLogs();
      fetchHealth();
    } catch (error) {
      toast.error('Failed to acknowledge logs');
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchHealth(), fetchLogs(), fetchNotificationSettings()]);
      setLastRefresh(new Date());
      setLoading(false);
    };
    loadData();
    
    // Check notification permission
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, [fetchHealth, fetchLogs, fetchNotificationSettings]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchHealth();
      fetchLogs();
    }, 60000);
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchHealth, fetchLogs]);

  // Refetch logs when filters change
  useEffect(() => {
    fetchLogs();
  }, [filterSeverity, filterType, showUnacknowledgedOnly, fetchLogs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="text-white">Loading monitoring data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white">
      {/* Header */}
      <header className="bg-[#1A1A1A] border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to Dashboard
            </Button>
            <div className="flex items-center gap-2">
              <Activity className="text-[#881337]" size={24} />
              <h1 className="text-xl font-semibold">System Monitoring</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {lastRefresh && (
              <span className="text-xs text-gray-500">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={refreshAll}
              disabled={refreshing}
              className="border-gray-700 text-gray-300"
            >
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant={notificationsEnabled ? "default" : "outline"}
              size="sm"
              onClick={requestNotificationPermission}
              className={notificationsEnabled ? "bg-[#881337]" : "border-gray-700 text-gray-300"}
            >
              {notificationsEnabled ? <Bell size={16} className="mr-2" /> : <BellOff size={16} className="mr-2" />}
              {notificationsEnabled ? 'Notifications On' : 'Enable Notifications'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* System Health Cards */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Server size={20} />
            System Health
            {health?.overall_status && (
              <span className="ml-2">
                <HealthIndicator status={health.overall_status} />
              </span>
            )}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Database */}
            <div className="bg-[#1A1A1A] rounded-xl p-5 border border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Database size={20} className="text-blue-400" />
                  <span className="font-medium">Database</span>
                </div>
                <HealthIndicator status={health?.components?.database?.status} />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className="capitalize">{health?.components?.database?.status || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Latency</span>
                  <span>{health?.components?.database?.latency_ms || 0}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Users</span>
                  <span>{health?.components?.database?.user_count || 0}</span>
                </div>
              </div>
            </div>

            {/* Stripe */}
            <div className="bg-[#1A1A1A] rounded-xl p-5 border border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard size={20} className="text-purple-400" />
                  <span className="font-medium">Stripe</span>
                </div>
                <HealthIndicator status={health?.components?.stripe?.status} />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Success Rate</span>
                  <span>{health?.components?.stripe?.success_rate || 100}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Webhooks (24h)</span>
                  <span>{health?.components?.stripe?.webhooks_24h || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Last Event</span>
                  <span className="text-xs truncate max-w-[120px]">
                    {health?.components?.stripe?.last_webhook_type || 'None'}
                  </span>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="bg-[#1A1A1A] rounded-xl p-5 border border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Mail size={20} className="text-green-400" />
                  <span className="font-medium">Email (Resend)</span>
                </div>
                <HealthIndicator status={health?.components?.email?.status} />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className="capitalize">{health?.components?.email?.status || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Sent (24h)</span>
                  <span>{health?.components?.email?.emails_24h || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Configured</span>
                  <span>{health?.components?.email?.configured ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>

            {/* API */}
            <div className="bg-[#1A1A1A] rounded-xl p-5 border border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Zap size={20} className="text-yellow-400" />
                  <span className="font-medium">API</span>
                </div>
                <HealthIndicator status={health?.components?.api?.status} />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Avg Response</span>
                  <span>{health?.components?.api?.avg_response_ms || 0}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Requests</span>
                  <span>{health?.components?.api?.total_requests || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Errors</span>
                  <span className={health?.components?.api?.total_errors > 0 ? 'text-red-400' : ''}>
                    {health?.components?.api?.total_errors || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Alert Summary */}
        {health?.unacknowledged_critical > 0 && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="text-red-400" size={24} />
              <span className="font-medium">
                {health.unacknowledged_critical} unacknowledged critical alert{health.unacknowledged_critical > 1 ? 's' : ''}
              </span>
            </div>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => acknowledgeAll('critical')}
            >
              Acknowledge All Critical
            </Button>
          </div>
        )}

        {/* Error Log Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle size={20} />
              System Logs
              <span className="text-sm font-normal text-gray-400">({logsTotal} total)</span>
            </h2>
            
            <div className="flex items-center gap-3">
              {/* Severity filter */}
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="bg-[#1A1A1A] border border-gray-700 rounded px-3 py-1.5 text-sm"
              >
                <option value="all">All Severities</option>
                <option value="critical">Critical ({severityCounts.critical || 0})</option>
                <option value="warning">Warning ({severityCounts.warning || 0})</option>
                <option value="info">Info ({severityCounts.info || 0})</option>
              </select>
              
              {/* Type filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="bg-[#1A1A1A] border border-gray-700 rounded px-3 py-1.5 text-sm"
              >
                <option value="all">All Types</option>
                <option value="payment_failure">Payment Failures</option>
                <option value="signup_error">Signup Errors</option>
                <option value="webhook_error">Webhook Errors</option>
                <option value="email_failure">Email Failures</option>
                <option value="new_signup">New Signups</option>
              </select>
              
              {/* Unacknowledged only toggle */}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showUnacknowledgedOnly}
                  onChange={(e) => setShowUnacknowledgedOnly(e.target.checked)}
                  className="rounded"
                />
                Unacknowledged only
              </label>
              
              {logs.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => acknowledgeAll()}
                  className="border-gray-700 text-gray-300"
                >
                  <Check size={14} className="mr-1" />
                  Ack All Visible
                </Button>
              )}
            </div>
          </div>

          {/* Log entries */}
          <div className="space-y-2">
            {logs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
                <p>No logs matching your filters</p>
                <p className="text-sm">System is running smoothly</p>
              </div>
            ) : (
              logs.map((log) => (
                <LogEntry 
                  key={log.id} 
                  log={log} 
                  onAcknowledge={acknowledgeLog}
                />
              ))
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminMonitoring;
